import { Virtuoso } from 'react-virtuoso';
import { useTranslation } from 'react-i18next';
import { Xmark } from '@peacecover/icon';
import { useChangeCurrentCastRelationship } from '../hooks/useChangeCurrentCastRelationship';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';
import type { components } from '../../../types/schema';

type CastRelationship = components['schemas']['ApiV1CastRelationship'];

interface PreviousCastRelationshipListProps {
  castRelationships: CastRelationship[];
  handleCreateCastRelationship: () => void;
  fetchNextPage: () => void;
}

const PreviousCastRelationshipList = ({
  castRelationships,
  handleCreateCastRelationship,
  fetchNextPage
}: PreviousCastRelationshipListProps) => {
  const { t } = useTranslation();
  return (
    <div className="mt-[32px] flex flex-col items-center">
      <button
        type="button"
        className="py-[11px] bg-primary-light text-[14px] font-semibold leading-[17px] text-pure-white rounded-full px-[18px] min-w-[286px]"
        onClick={handleCreateCastRelationship}
      >
        {t('CastRelationship.changeNewCast')}
      </button>
      <p className="text-[15px] font-semibold leading-[18px] text-main-dark-2 mt-[53px] mb-[20px]">
        {t('CastRelationship.previousList')}
      </p>
      <div className="px-[24px] w-full">
        <Virtuoso
          useWindowScroll
          style={{ width: '100%' }}
          data={castRelationships}
          endReached={fetchNextPage}
          itemContent={(_, castRelationship) => (
            <div className="mb-[10px]">
              <PreviousCastRelationshipItem
                key={castRelationship.castRelationshipId}
                castRelationship={castRelationship}
              />
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default PreviousCastRelationshipList;

const PreviousCastRelationshipItem = ({
  castRelationship
}: {
  castRelationship: CastRelationship;
}) => {
  const { t } = useTranslation();
  const { handleUpdateCastRelationship } = useChangeCurrentCastRelationship({
    castRelationshipId: castRelationship.castRelationshipId
  });

  return (
    <div className="p-[20px] bg-backgroundElevated-dark rounded-[16px] border border-pure-black shadow-[0_0_2px_0_rgba(0,0,0,0.05),0_0_8px_0_rgba(0,0,0,0.07)] w-full flex items-center justify-between">
      <div className="flex items-center gap-[7px]">
        <div className="flex items-center gap-[4px]">
          <ProfileAvatar
            thumbnailUrl={castRelationship.personAThumbnailUrl}
            widthAndHeight="27px"
            backgroundColor="#504D7F"
          />
          <p className="text-[12px] font-medium leading-[14px] text-main-dark-2">
            {castRelationship.personAName}
          </p>
        </div>
        <Xmark size={12} className="text-main-dark-3" />
        <div className="flex items-center gap-[4px]">
          <ProfileAvatar
            thumbnailUrl={castRelationship.personBThumbnailUrl}
            widthAndHeight="27px"
            backgroundColor="#504D7F"
          />
          <p className="text-[12px] font-medium leading-[14px] text-main-dark-2">
            {castRelationship.personBName}
          </p>
        </div>
      </div>
      <button
        type="button"
        className="px-[12px] py-[6px] rounded-full bg-[rgba(255,255,255,0.10)] text-[13px] font-medium leading-[16px] text-onLabel-dark"
        onClick={handleUpdateCastRelationship}
      >
        {t('CastRelationship.select')}
      </button>
    </div>
  );
};
